import AppCard from '@nxsys/components/AppCard';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ImageTech05 } from '@nxsys/assets/images';
import AppFormItem from '@nxsys/components/AppFormItem';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import AppInput from '@nxsys/components/AppInput';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import { useSelector } from 'react-redux';
import { RootState } from '@nxsys/store';
import { Moment } from 'moment';
import { roles } from '@nxsys/store/constants';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type HMRCUpdateFilterHeaderProps = {
  companyRefs: OptionType | null;
  onCompanyRefsChange: (value: OptionType | null) => void;
  employeeRefs: OptionType | null;
  onEmployeeRefsChange: (value: OptionType | null) => void;
  issueDateFrom: string | null;
  onIssueDateFromChange: (value: string | null) => void;
  issueDateTo: string | null;
  onIssueDateToChange: (value: string | null) => void;
  startDate: string | null;
  onStartDateChange: (value: string | null) => void;
  endDate: string | null;
  onEndDateChange: (value: string | null) => void;
  effectiveDateFrom: string | null;
  onEffectiveDateFromChange: (value: string | null) => void;
  effectiveDateTo: string | null;
  onEffectiveDateToChange: (value: string | null) => void;
  NINumber: string | null;
  onNINumberChange: (value: string | null) => void;
  isP6P9?: boolean;
};

export const HMRCUpdateFilterHeader = ({
  companyRefs,
  onCompanyRefsChange,
  employeeRefs,
  onEmployeeRefsChange,
  issueDateFrom,
  onIssueDateFromChange,
  // issueDateTo,
  // onIssueDateToChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  effectiveDateFrom,
  onEffectiveDateFromChange,
  effectiveDateTo,
  onEffectiveDateToChange,
  NINumber,
  onNINumberChange,
  isP6P9 = false,
}: HMRCUpdateFilterHeaderProps) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const { user, isAgency } = useSelector(
    (state: RootState) => state.appStore,
  );
  const isAdmin = user?.role === roles.admin;

  const handleCompanyRefsChange = (value: OptionType | OptionType[] | null) => {
    onCompanyRefsChange((value as OptionType) || null);
  };

  const handleEmployeeRefsChange = (
    value: OptionType | OptionType[] | null,
  ) => {
    onEmployeeRefsChange((value as OptionType) || null);
  };

  const handleIssueDateFromChange = (date: Moment | null) => {
    onIssueDateFromChange(date?.toISOString() || null);
  };

  // const handleIssueDateToChange = (date: Moment | null) => {
  //   onIssueDateToChange(date?.toISOString() || null);
  // };

  const handleStartDateChange = (date: Moment | null) => {
    onStartDateChange(date?.toISOString() || null);
  };

  const handleEndDateChange = (date: Moment | null) => {
    onEndDateChange(date?.toISOString() || null);
  };

  const handleEffectiveDateFromChange = (date: Moment | null) => {
    onEffectiveDateFromChange(date?.toISOString() || null);
  };

  const handleEffectiveDateToChange = (date: Moment | null) => {
    onEffectiveDateToChange(date?.toISOString() || null);
  };

  const handleNINumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNINumberChange(event.target.value);
  };

  return (
    <AppCard className="flex flex-col gap-2 p-4 mb-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 border-b border-neutral-100 w-full pb-2 mb-2">
          <div className="flex items-center justify-center gap-2 w-14 h-14 rounded-full bg-gradient-to-b from-[rgba(255,247,224,0.4)] to-[rgba(255,237,185,1)]">
            <img src={ImageTech05} alt="hmrc-logo" className="w-10 h-10" />
          </div>
          <div>
            <div className="text-lg font-bold">
              {messages['common.searchInformation']}
            </div>
            <div className="text-sm text-neutral-400">
              {messages['common.quickFilter']}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row flex-wrap w-full gap-2">
        {(isAdmin || isAgency) && (
          <AppFormItem
            label={employerLabels.label}
            labelPosition="top"
            className="!w-full md:!w-[280px] sm:!w-full"
          >
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              value={companyRefs}
              placeholder={employerLabels.placeholder}
              onChangeValue={handleCompanyRefsChange}
              className="!w-full"
            />
          </AppFormItem>
        )}
        {!isP6P9 && (
          <>
            <AppFormItem
              label={messages['common.employeeName']}
              labelPosition="top"
              className="!w-full md:!w-[280px] sm:!w-full"
            >
              <AppSelectSync
                type="emp&sub"
                apiType="searchEmployeeSubcontractV2"
                value={employeeRefs}
                placeholder={messages['common.employeeName']}
                onChangeValue={handleEmployeeRefsChange}
                className="!w-full"
              />
            </AppFormItem>

            <AppFormItem
              label={messages['common.NINumber']}
              labelPosition="top"
              className="!w-full md:!w-[280px] sm:!w-full"
            >
              <AppInput
                placeholder={messages['common.NINumber']}
                className="!w-full"
                value={NINumber || ''}
                onChange={handleNINumberChange}
              />
            </AppFormItem>
          </>
        )}

        <AppFormItem
          label={messages['common.issueDate']}
          labelPosition="top"
          className="!w-full md:!w-[280px] sm:!w-full"
        >
          <AppDatePicker
            placeholder={messages['common.issueDate']}
            className="!w-full"
            value={issueDateFrom}
            onChange={handleIssueDateFromChange}
            format="DD/MM/YYYY"
          />
        </AppFormItem>

        {/* <AppFormItem
          label={messages['common.issueDateTo']}
          labelPosition="top"
          className="!w-full md:!w-[280px] sm:!w-full"
        >
          <AppDatePicker
            placeholder={messages['common.issueDateTo']}
            className="!w-full"
            value={issueDateTo}
            onChange={handleIssueDateToChange}
            format="DD/MM/YYYY"
          />
        </AppFormItem> */}

        {isP6P9 ? (
          <>
            <AppFormItem
              label={messages['common.effectiveDateFrom']}
              labelPosition="top"
              className="!w-full md:!w-[280px] sm:!w-full"
            >
              <AppDatePicker
                placeholder={messages['common.effectiveDateFrom']}
                className="!w-full"
                value={effectiveDateFrom}
                onChange={handleEffectiveDateFromChange}
                format="DD/MM/YYYY"
              />
            </AppFormItem>

            <AppFormItem
              label={messages['common.effectiveDateTo']}
              labelPosition="top"
              className="!w-full md:!w-[280px] sm:!w-full"
            >
              <AppDatePicker
                placeholder={messages['common.effectiveDateTo']}
                className="!w-full"
                value={effectiveDateTo}
                onChange={handleEffectiveDateToChange}
                format="DD/MM/YYYY"
              />
            </AppFormItem>
          </>
        ) : (
          <>
            <AppFormItem
              label={messages['common.startDate']}
              labelPosition="top"
              className="!w-full md:!w-[280px] sm:!w-full"
            >
              <AppDatePicker
                placeholder={messages['common.startDate']}
                className="!w-full"
                value={startDate}
                onChange={handleStartDateChange}
                format="DD/MM/YYYY"
              />
            </AppFormItem>

            <AppFormItem
              label={messages['common.endDate']}
              labelPosition="top"
              className="!w-full md:!w-[280px] sm:!w-full"
            >
              <AppDatePicker
                placeholder={messages['common.endDate']}
                className="!w-full"
                value={endDate}
                onChange={handleEndDateChange}
                format="DD/MM/YYYY"
              />
            </AppFormItem>
          </>
        )}
      </div>
    </AppCard>
  );
};
